/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ConnectionRequestsView` for the Caintra Connect frontend.
 * @module components/Chat/ConnectionRequestsView
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import styles from "../../styles/Chat.module.css";
import { ReactComponent as MoreIcon } from "../../assets/icons/Chat/MoreIcon.svg";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import {
  handleAcceptRequest,
  handleRejectRequest,
  handleViewProfile,
} from "../../logic/chat";

const ConnectionRequestsView = ({
  connectionRequests,
  onViewChange,
  activeView,
  chats,
  onChatsUpdate,
  onChatParticipantsUpdate,
  onConnectionRequestsUpdate,
  navigate,
  onSimulatedChatOpen,
}) => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("below");

  // Utility function to calculate optimal dropdown position
  const calculateDropdownPosition = (buttonRect) => {
    const dropdownHeight = 80; // Approximate height of dropdown menu
    const dropdownWidth = 90; // Width of dropdown menu for chat
    const padding = 4; // Padding from button
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Calculate available space below and above the button
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Determine if dropdown should open above or below
    const shouldOpenAbove =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    // Calculate vertical position
    const top = shouldOpenAbove
      ? buttonRect.top + window.scrollY - dropdownHeight - padding
      : buttonRect.bottom + window.scrollY + padding;

    // Calculate horizontal position (ensure it doesn't go off screen)
    let left = buttonRect.right - dropdownWidth;
    if (left < 0) {
      left = buttonRect.left;
    }
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 10; // 10px margin from edge
    }

    return {
      top,
      left,
      shouldOpenAbove,
    };
  };

  const toggleDropdown = (requestId, event) => {
    event.stopPropagation();
    logger.info("Toggling dropdown for request:", {
      data: [requestId, "Current open:", openDropdown],
    });

    if (openDropdown === requestId) {
      setOpenDropdown(null);
      setActiveDropdownId(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const position = calculateDropdownPosition(rect);

      setDropdownPosition({
        top: position.top,
        left: position.left,
      });
      setDropdownDirection(position.shouldOpenAbove ? "above" : "below");
      setOpenDropdown(requestId);
      setActiveDropdownId(requestId);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        !event.target.closest(`.${styles.dropdownButton}`) &&
        !event.target.closest(`.${styles.dropdownMenu}`)
      ) {
        setOpenDropdown(null);
        setActiveDropdownId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const handleAccept = () => {
    handleAcceptRequest(
      activeDropdownId,
      connectionRequests,
      navigate,
      onConnectionRequestsUpdate,
      onChatsUpdate,
      onChatParticipantsUpdate,
    );
    setOpenDropdown(null);
    setActiveDropdownId(null);
  };

  const handleReject = () => {
    handleRejectRequest(
      activeDropdownId,
      connectionRequests,
      navigate,
      onConnectionRequestsUpdate,
    );
    setOpenDropdown(null);
    setActiveDropdownId(null);
  };

  const handleViewProfileClick = () => {
    const request = connectionRequests.find(
      (req) => req._id === activeDropdownId,
    );
    if (request) {
      handleViewProfile(request, navigate);
    }
    setOpenDropdown(null);
    setActiveDropdownId(null);
  };

  return (
    <div className={styles.inboxContainer}>
      <div className={styles.inboxHeader}>
        <div className={styles.viewToggleButtons}>
          <button
            className={`${styles.viewToggleButton} ${
              activeView === "inbox" ? styles.activeButton : ""
            }`}
            onClick={() => onViewChange("inbox")}
          >
            <span>{t("pages.chat.inbox")}</span>
            {chats.filter((chat) => !chat.lastMessageSeen).length > 0 && (
              <span className={styles.requestBadge}>
                {chats.filter((chat) => !chat.lastMessageSeen).length}
              </span>
            )}
          </button>
          <button
            className={`${styles.viewToggleButton} ${
              activeView === "requests" ? styles.activeButton : ""
            }`}
            onClick={() => onViewChange("requests")}
          >
            <span>{t("pages.chat.requests")}</span>
            {connectionRequests.length > 0 && (
              <span className={styles.requestBadge}>
                {connectionRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>
      <div className={styles.messagesListContainer}>
        {connectionRequests.length > 0 ? (
          connectionRequests.map((request) => (
            <div key={request._id} className={styles.messageContainer}>
              <div
                className={styles.messageItem}
                onClick={() => {
                  if (
                    request?.type === "businessEventConnectionRequest" ||
                    request?.type === "customerCompanyUserConnectionRequest"
                  ) {
                    onSimulatedChatOpen && onSimulatedChatOpen(request);
                  }
                }}
              >
                <div className={styles.messageItemAvatar}>
                  <img
                    className={styles.avatar}
                    src={Caintra_Logo_03}
                    alt={t("common.avatarAlt")}
                  />
                </div>
                <div className={styles.messageItemContent}>
                  <div className={styles.messageItemSender}>
                    {request.empresa}
                  </div>
                  <div className={styles.senderInfo}>
                    <span className={styles.senderInfoName}>
                      {request.nombre}
                    </span>
                    {request.type !== "businessEventConnectionRequest" &&
                      request.type !==
                        "customerCompanyUserConnectionRequest" && (
                        <span className={styles.senderInfoEmail}>
                          ({request.correoElectronico})
                        </span>
                      )}
                  </div>
                </div>
                <div className={styles.requestActions}>
                  <button
                    className={styles.dropdownButton}
                    onClick={(e) => toggleDropdown(request._id, e)}
                  >
                    <MoreIcon />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.noContent}>{t("pages.chat.noRequests")}</div>
        )}
      </div>

      {/* Portal-based dropdown menu */}
      {openDropdown &&
        activeDropdownId &&
        ReactDOM.createPortal(
          <div
            className={`${styles.dropdownMenu} ${
              dropdownDirection === "above"
                ? styles.dropdownAbove
                : styles.dropdownBelow
            }`}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 999999,
            }}
          >
            <button className={styles.dropdownItem} onClick={handleAccept}>
              {t("pages.chat.accept")}
            </button>
            <button
              className={styles.dropdownItem}
              onClick={handleViewProfileClick}
            >
              {t("pages.chat.viewProfile")}
            </button>
            <div className={styles.dropdownDivider}></div>
            <button
              className={`${styles.dropdownItem} ${styles.rejectItem}`}
              onClick={handleReject}
            >
              {t("pages.chat.reject")}
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ConnectionRequestsView;
